<template>
  <div 
    class="task-card" 
    :class="{ 
      'has-active-users': hasActiveUsers,
      'light-background': isLightColor(task.cor || '#6366f1'),
      'dark-background': !isLightColor(task.cor || '#6366f1')
    }"
    @click="handleCardClick"
    @touchstart.passive="onTouchStart"
    @touchmove.passive="onTouchMove"
    @touchend="onTouchEnd"
    @contextmenu.prevent="showContextMenu"
    :style="{ backgroundColor: task.cor || '#6366f1' }"
  >
    <!-- Título da Task -->
      <div class="task-title">
      <h4 v-html="formatTitleForCard(task.titulo)"></h4>
      </div>
      
    <!-- Descrição (condicional) -->
    <div v-if="showDescription && task.descricao" class="task-description">
      <p>{{ task.descricao }}</p>
    </div>
    
    <!-- Tags -->
    <div v-if="hasTagsToShow" class="task-tags">
      <!-- Modo Expandido - Por Categoria -->
      <div v-if="showTagsExpanded" class="tags-expanded">
        <div 
          v-for="category in categoriesWithTags" 
          :key="category.id" 
          class="tag-category"
        >
          <span class="category-name">{{ category.nome }}:</span>
          <div class="category-tags">
            <span 
              v-for="tag in category.tags" 
              :key="tag.id" 
              class="tag-badge"
              :style="{ backgroundColor: tag.cor || '#6366f1', color: getContrastColor(tag.cor || '#6366f1') }"
            >
              <span class="tag-icon">{{ getTagIcon(tag) }}</span>
              {{ tag.nome }}
      </span>
    </div>
        </div>
    </div>
    
      <!-- Modo Minimizado - Apenas Tags (2-3 + ...) -->
      <div v-else class="tags-minimized">
        <span 
          v-for="(tag, index) in visibleTagsMinimized" 
          :key="tag.id" 
          class="tag-badge"
          :style="{ backgroundColor: tag.cor || '#6366f1', color: getContrastColor(tag.cor || '#6366f1') }"
        >
          <span class="tag-icon">{{ getTagIcon(tag) }}</span>
          {{ tag.nome }}
      </span>
        <span v-if="hasMoreTags" class="more-tags">...</span>
      </div>
    </div>
    
    <!-- Usuários Atribuídos -->
    <div v-if="showUsers && hasUsersToShow" class="task-users">
      <div class="users-container">
        <div 
          v-for="user in assignedUsers" 
          :key="user.id" 
          class="user-avatar"
          :title="userLabel(user)"
          @mouseenter="handleMouseEnter(user, $event)"
          @mouseleave="handleMouseLeave(user)"
        >
          <img 
            v-if="user.fotoPerfil" 
            :src="mediaSrc(user.fotoPerfil)" 
            :alt="userLabel(user)"
            class="user-image"
          />
          <div 
            v-else 
            class="user-initials"
            :style="{ backgroundColor: getUserColor(userLabel(user)) }"
          >
            {{ getUserInitials(userLabel(user)) }}
        </div>
        </div>
        <div v-if="hasMoreUsers" class="more-users">
          +{{ assignedUsers.length - 3 }}
        </div>
      </div>
    </div>
    
    <!-- Controles de Atividade -->
    <div v-if="showActivityControls && hasActiveUsers" class="task-activity-controls">
      <div class="activity-header">
        <div class="activity-title">
          <svg class="activity-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
      </svg>
          <span>{{ t('kanban.taskCard.working') }}</span>
    </div>
        <div class="activity-count">{{ activeUsersCount }}</div>
      </div>
      <div class="activity-users-container">
        <div 
          v-for="user in activeUsersList" 
          :key="user.id" 
          class="activity-user-item"
        >
          <div class="activity-user-info">
            <div class="activity-user-avatar">
              <img 
                v-if="user.fotoPerfil" 
                :src="mediaSrc(user.fotoPerfil)" 
                :alt="user.nomeCompleto"
                class="activity-user-image"
              />
              <div 
                v-else 
                class="activity-user-initials"
                :style="{ backgroundColor: getUserColor(userLabel(user)) }"
              >
                {{ getUserInitials(userLabel(user)) }}
              </div>
            </div>
            <div class="activity-user-details">
              <div class="activity-user-name">
                {{ userLabel(user) }}
              </div>
              <div class="activity-status">
                <span class="status-indicator active"></span>
                <span class="status-text">{{ t('kanban.taskCard.working') }}</span>
              </div>
            </div>
          </div>
          <div class="activity-indicator">
            <div class="activity-pulse">
              <svg class="activity-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
        </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Agendamento -->
    <!-- key única para evitar re-renderização desnecessária -->
    <div 
      v-if="showSchedule && isScheduled" 
      :key="`schedule-${task.id}-${formattedScheduleDate}-${formattedScheduleTime}-${motivoCompleto?.id || 'none'}`"
      class="task-schedule schedule-stable"
    >
      <div class="schedule-header-bar">
        <svg class="schedule-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
      </svg>
        <span class="schedule-header-text">{{ t('kanban.taskCard.schedule') }}</span>
    </div>
    
      <div class="schedule-datetime">
        <div class="schedule-date-time-item">
          <svg class="datetime-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <span class="datetime-value schedule-text-stable">{{ formattedScheduleDate }}</span>
  </div>
        <div class="schedule-separator">•</div>
        <div class="schedule-date-time-item">
          <svg class="datetime-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span class="datetime-value schedule-text-stable">{{ formattedScheduleTime }}</span>
        </div>
      </div>
      
      <div v-if="motivoCompleto" class="schedule-reason-box">
        <div class="reason-card schedule-reason-stable">
          <div class="reason-icon schedule-icon-stable" :style="{ backgroundColor: motivoCompleto.cor }">
            <span>{{ getMotivoIcon(motivoCompleto.icone) }}</span>
          </div>
          <div class="reason-content">
            <h3 class="schedule-text-stable">{{ motivoCompleto.nome }}</h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Seção de Checklist -->
    <div v-if="showChecklist && hasChecklistToShow" class="task-checklist">
        <div class="checklist-header">
          <svg class="checklist-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
        </svg>
          <span class="checklist-title">{{ t('kanban.taskCard.checklist') }}</span>
          <span class="checklist-total-count">{{ checklistTotal.completed }}/{{ checklistTotal.total }}</span>
        </div>
        
        <!-- Categorias do Checklist -->
        <div class="checklist-categories">
          <div v-for="category in checklistCategories" :key="category.id" class="checklist-category">
            <div class="category-info">
              <span class="category-name">{{ category.nome }}</span>
              <span class="category-count">{{ category.completed }}/{{ category.total }}</span>
            </div>
            <div class="category-progress-bar">
              <div 
                class="category-progress-fill" 
                :style="{ width: category.percentage + '%' }"
              ></div>
            </div>
            <span class="category-percentage">{{ category.percentage }}%</span>
          </div>
         </div>
     </div>
     <!-- Fim da Seção de Checklist -->

    <!-- Seção de Anexos e Imagens -->
    <!-- v-memo otimiza re-renderização -->
    <div 
      v-if="showAttachments && anexosStats.hasAny" 
      v-memo="[anexosStats.total]"
      class="task-attachments"
    >
      <div class="attachments-header">
        <svg class="attachments-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
        </svg>
        <span class="attachments-title">{{ t('kanban.taskCard.attachments') }}</span>
      </div>
      
      <div class="attachments-items">
        <!-- Imagens -->
        <div v-if="anexosStats.imagens.hasItems" class="attachment-item">
          <svg class="attachment-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <span class="attachment-item-label">{{ t('kanban.taskCard.images') }}</span>
          <span class="attachment-item-count">{{ anexosStats.imagens.count }}</span>
        </div>
        
        <!-- Arquivos -->
        <div v-if="anexosStats.arquivos.hasItems" class="attachment-item">
          <svg class="attachment-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <span class="attachment-item-label">{{ t('kanban.taskCard.files') }}</span>
          <span class="attachment-item-count">{{ anexosStats.arquivos.count }}</span>
        </div>
      </div>
    </div>
    <!-- Fim da Seção de Anexos -->

    <!-- Seção Financeira (Recebíveis) -->
    <div v-if="showFinanceiro && recebiveisStats.quantidade > 0" class="task-financeiro">
      <div class="financeiro-header">
        <svg class="financeiro-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span class="financeiro-title">{{ t('kanban.taskCard.financial') }}</span>
      </div>
      <div class="financeiro-items">
        <div class="financeiro-item premium-row">
          <span class="financeiro-label">{{ t('kanban.taskCard.receivables') }}</span>
          <span class="financeiro-value">{{ recebiveisStats.quantidade }}</span>
        </div>
        <div class="financeiro-item premium-row">
          <span class="financeiro-label">{{ t('kanban.taskCard.received') }}</span>
          <MoneyAmount :value="recebiveisStats.valorRecebido" size="sm" class="financeiro-value" />
        </div>
        <div class="financeiro-item premium-row">
          <span class="financeiro-label">{{ t('kanban.taskCard.pending') }}</span>
          <MoneyAmount :value="recebiveisStats.valorPendente" size="sm" class="financeiro-value" />
        </div>
      </div>
    </div>

    <!-- Seção Progresso da Tarefa -->
    <div v-if="showTaskProgress" class="task-financeiro">
      <div class="financeiro-header">
        <svg class="financeiro-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-6m4 6V7m4 10v-3M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z"></path>
        </svg>
        <span class="financeiro-title">{{ t('kanban.taskCard.progress') }}</span>
      </div>
      <div class="financeiro-items">
        <div class="financeiro-item financeiro-progress-item">
          <div class="financeiro-progress-header">
            <span class="financeiro-label">{{ t('kanban.taskCard.completed') }}</span>
            <span class="financeiro-value" :class="progressClass">{{ task.progressoTarefaPercentual ?? 0 }}%</span>
          </div>
          <div class="financeiro-progress">
            <div class="financeiro-progress-bar" :class="progressBarClass" :style="{ width: `${Math.min(100, Math.max(0, task.progressoTarefaPercentual ?? 0))}%` }"></div>
          </div>
        </div>
        <div class="financeiro-item premium-row">
            <span class="financeiro-label">{{ t('kanban.taskCard.deadline') }}</span>
          <span class="financeiro-value prazo-value" :class="prazoClass">
            <span class="prazo-icon">{{ prazoIcon }}</span>
            {{ formatDate(task.prazoTarefa) || '--' }}
          </span>
        </div>
      </div>
    </div>
    <!-- Fim da Seção Financeira -->

    <!-- Datas de criação / atualização (sempre no rodapé do card) -->
    <div v-if="(showCreatedAt && createdAtValue) || (showUpdatedAt && updatedAtValue)" class="task-meta-dates">
      <div class="task-meta-dates-row">
        <div v-if="showCreatedAt && createdAtValue" class="task-meta-date-item">
          <span class="task-meta-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
          </span>
          <!-- <span class="task-meta-label"></span> -->
          <span class="task-meta-value">{{ formatDate(createdAtValue) }}</span>
        </div>
        <div v-if="showUpdatedAt && updatedAtValue" class="task-meta-date-item">
          <span class="task-meta-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </span>
          <!-- <span class="task-meta-label"></span> -->
          <span class="task-meta-value">{{ formatDate(updatedAtValue) }}</span>
        </div>
      </div>
    </div>

  </div>
  <!-- Fim do Card Task do Kanban -->
  
 
    
  <!-- Tooltip flutuante usando Teleport -->
  <Teleport to="body">
    <div 
      v-if="showTooltip" 
      class="floating-tooltip"
      :style="{ 
        left: tooltipPosition.x + 'px', 
        top: tooltipPosition.y + 'px'
      }"
    >
      <div class="tooltip-content">
        <div class="tooltip-avatar">
          <img 
            v-if="tooltipUser?.fotoPerfil" 
            :src="mediaSrc(tooltipUser.fotoPerfil)" 
            :alt="tooltipUser.nomeCompleto"
            class="tooltip-image"
          />
          <div 
            v-else 
            class="tooltip-initials"
            :style="{ backgroundColor: getUserColor(tooltipUser?.nomeCompleto || '') }"
          >
            {{ getUserInitials(tooltipUser?.nomeCompleto || '') }}
    </div>
  </div>
        <div class="tooltip-info">
          <div class="tooltip-name">
            {{ tooltipUser?.nomeCompleto || t('kanban.taskCard.user') }}
          </div>
          <div class="tooltip-email">
            {{ tooltipUser?.email || 'email@exemplo.com' }}
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Menu de Contexto (Clique Direito) -->
  <Teleport to="body">
    <div 
      v-if="contextMenuVisible" 
      class="context-menu"
      :style="{ top: contextMenuY + 'px', left: contextMenuX + 'px' }"
      @click.stop
    >
      <div v-if="canArchiveCards" class="context-menu-item" @click="arquivarTask">
        <svg class="context-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
        </svg>
        {{ t('kanban.taskCard.archiveTask') }}
      </div>
    </div>
  </Teleport>
    
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast, POSITION } from 'vue-toastification'
import { getTagIcon as getIconFromMapper } from '@/utils/iconMapper'
import { resolveMediaUrl } from '@/config/app'
import MoneyAmount from './MoneyAmount.vue'
import { useMobileCardTap } from '@/composables/useMobileCardTap.js'

export default {
  name: 'TaskCard',
  components: { MoneyAmount },
  props: {
    task: {
      type: Object,
      required: true
    },
    showDescription: {
      type: Boolean,
      default: false
    },
    showTagsExpanded: {
      type: Boolean,
      default: false
    },
    showUsers: {
      type: Boolean,
      default: false
    },
    showActivityControls: {
      type: Boolean,
      default: false
    },
    showSchedule: {
      type: Boolean,
      default: false
    },
    showChecklist: {
      type: Boolean,
      default: false
    },
    showAttachments: {
      type: Boolean,
      default: false
    },
    showFinanceiro: {
      type: Boolean,
      default: false
    },
    showTaskProgress: {
      type: Boolean,
      default: false
    },
    showCreatedAt: {
      type: Boolean,
      default: false
    },
    showUpdatedAt: {
      type: Boolean,
      default: false
    },
    workspaceTags: {
      type: Array,
      default: () => []
    },
    workspaceUsers: {
      type: Array,
      default: () => []
    },
    workspaceMotivos: {
      type: Array,
      default: () => []
    },
    canArchiveCards: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update', 'delete', 'open-detail', 'archived'],
  setup(props, { emit }) {
    const toast = useToast()
    const { t, locale } = useI18n()
    const userLabel = (user) => user.nomeCompleto || t('kanban.taskCard.userFallback', { id: user.id })
    const mediaSrc = (url) => resolveMediaUrl(url)

    const dateLocale = computed(() => {
      if (locale.value === 'en') return 'en-US'
      if (locale.value === 'es') return 'es'
      return 'pt-BR'
    })

    const formatDate = (dateInput) => {
      if (!dateInput) return ''
      try {
        let d
        if (Array.isArray(dateInput)) {
          const [ano, mes, dia, hora = 0, minuto = 0, segundo = 0] = dateInput
          d = new Date(ano, mes - 1, dia, hora, minuto, segundo)
        } else {
          const raw = String(dateInput).split('.')[0]
          d = new Date(raw)
        }
        if (Number.isNaN(d.getTime())) return ''
        return d.toLocaleDateString(dateLocale.value)
      } catch (e) {
        return ''
      }
    }

    const formatTitleForCard = (title) => {
      if (!title) return ''
      const escaped = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return escaped.replace(/\n/g, '<br>')
    }

    // Datas derivadas para tarefas (suporta diferentes nomes de campos)
    const createdAtValue = computed(() => {
      return props.task.dataCriacao || props.task.dataCriacaoTask || props.task.createdAt || null
    })

    const updatedAtValue = computed(() => {
      return props.task.dataUpdate || props.task.dataAtualizacao || props.task.updatedAt || null
    })

    const parsedRecebiveis = computed(() => {
      try {
        const raw = props.task.recebiveis
        if (!raw) return []
        if (Array.isArray(raw)) return raw
        if (typeof raw === 'string') return JSON.parse(raw)
        return []
      } catch {
        return []
      }
    })

    const recebiveisStats = computed(() => {
      return parsedRecebiveis.value.reduce((acc, item) => {
        const valor = Number(item?.valor || 0)
        acc.quantidade += 1
        if (item?.pago) acc.valorRecebido += valor
        else acc.valorPendente += valor
        return acc
      }, { quantidade: 0, valorRecebido: 0, valorPendente: 0 })
    })

    const progressClass = computed(() => {
      const pct = parseFloat(props.task.progressoTarefaPercentual) || 0
      if (pct >= 80) return 'progress-high'
      if (pct >= 50) return 'progress-medium'
      return 'progress-low'
    })

    const progressBarClass = computed(() => {
      const pct = parseFloat(props.task.progressoTarefaPercentual) || 0
      if (pct >= 80) return 'bar-high'
      if (pct >= 50) return 'bar-medium'
      return 'bar-low'
    })

    const prazoStatus = computed(() => {
      const prazo = props.task?.prazoTarefa
      if (!prazo) return 'neutral'
      const hoje = new Date()
      hoje.setHours(0, 0, 0, 0)
      const dataPrazo = new Date(String(prazo).split('T')[0] + 'T00:00:00')
      if (Number.isNaN(dataPrazo.getTime())) return 'neutral'
      const diffDias = Math.ceil((dataPrazo.getTime() - hoje.getTime()) / 86400000)
      if (diffDias < 0) return 'overdue'
      if (diffDias <= 5) return 'near'
      return 'ok'
    })

    const prazoIcon = computed(() => {
      if (prazoStatus.value === 'overdue') return '⚠'
      if (prazoStatus.value === 'near') return '⏳'
      if (prazoStatus.value === 'ok') return '📅'
      return '•'
    })

    const prazoClass = computed(() => {
      if (prazoStatus.value === 'overdue') return 'prazo-overdue'
      if (prazoStatus.value === 'near') return 'prazo-near'
      if (prazoStatus.value === 'ok') return 'prazo-ok'
      return 'prazo-neutral'
    })

    const openDetailModal = () => {
      emit('open-detail', props.task)
    }

    const {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onClick: handleCardClick
    } = useMobileCardTap(openDetailModal)

    // Menu de contexto (clique direito)
    const contextMenuVisible = ref(false)
    const contextMenuX = ref(0)
    const contextMenuY = ref(0)

    const showContextMenu = (event) => {
      event.preventDefault()
      contextMenuX.value = event.clientX
      contextMenuY.value = event.clientY
      contextMenuVisible.value = true
      
      // Fechar menu ao clicar em qualquer lugar
      const closeMenu = () => {
        contextMenuVisible.value = false
        document.removeEventListener('click', closeMenu)
      }
      document.addEventListener('click', closeMenu)
    }

    const arquivarTask = async () => {
      const Swal = (await import('sweetalert2')).default

      const result = await Swal.fire({
        title: t('kanban.taskCard.archiveTitle'),
        html: `
          <p style="margin-bottom: 12px;">${t('kanban.taskCard.archiveConfirm')}</p>
          <p style="color: #ef4444; font-weight: 500;">⚠️ ${t('kanban.taskCard.archiveActivityWarning')}</p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: t('kanban.taskCard.confirmArchive'),
        cancelButtonText: t('common.cancel'),
        reverseButtons: true,
        customClass: {
          popup: 'swal-popup-custom',
          title: 'swal-title-custom',
          htmlContainer: 'swal-html-custom'
        }
      })

      if (!result.isConfirmed) {
        contextMenuVisible.value = false
        return
      }

      try {
        const api = await import('@/services/api').then(m => m.default)
        await api.put(`/tasks/${props.task.id}/arquivar`)
        
        console.log('✅ Task arquivada com sucesso')

        toast.success(t('kanban.taskCard.archivedText'), {
          position: POSITION.BOTTOM_RIGHT,
          timeout: 3000
        })

        emit('archived', props.task.id)
        
      } catch (error) {
        console.error('❌ Erro ao arquivar task:', error)
        
        await Swal.fire({
          title: t('kanban.taskCard.archiveErrorTitle'),
          text: t('kanban.taskCard.archiveErrorText'),
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#ef4444'
        })
      } finally {
        contextMenuVisible.value = false
      }
    }

    // Função para determinar se a cor é clara ou escura
    const isLightColor = (hexColor) => {
      if (!hexColor) return true
      const hex = hexColor.replace('#', '')
      const r = parseInt(hex.substr(0, 2), 16)
      const g = parseInt(hex.substr(2, 2), 16)
      const b = parseInt(hex.substr(4, 2), 16)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return luminance > 0.5
    }

    // Variáveis para o tooltip flutuante
    const showTooltip = ref(false)
    const tooltipUser = ref(null)
    const tooltipPosition = ref({ x: 0, y: 0 })

    // Variáveis para controles de atividade
    const activeUsers = ref(new Set())
    
    // Parse das tags (JSON) e buscar tags completas do workspace
    const parsedTags = computed(() => {
      try {
        console.log('🔍 TaskCard - task.tags:', props.task.tags)
        console.log('🔍 TaskCard - workspaceTags:', props.workspaceTags)
        
        if (!props.task.tags) {
          console.log('❌ TaskCard - Sem tags na task')
          return []
        }
        
        // Parse dos IDs das tags da task
        const tagIds = typeof props.task.tags === 'string' ? JSON.parse(props.task.tags) : props.task.tags
        console.log('✅ TaskCard - IDs das tags:', tagIds)
        
        if (!Array.isArray(tagIds)) {
          return []
        }
        
        // Buscar as tags completas do workspace
        const allTags = []
        props.workspaceTags.forEach(categoria => {
          if (categoria.tags) {
            categoria.tags.forEach(tag => {
              allTags.push({
                ...tag,
                categoria: {
                  id: categoria.id,
                  nome: categoria.nome
                }
              })
            })
          }
        })
        
        // Filtrar apenas as tags que estão selecionadas na task
        const selectedTags = allTags.filter(tag => tagIds.includes(tag.id))
        
        
        return selectedTags
      } catch (e) {
        console.error('❌ Erro ao parsear tags:', e)
        console.error('❌ Valor das tags:', props.task.tags)
        return []
      }
    })
    
    // Agrupar tags por categoria mantendo a ordem do workspace
    const categoriesWithTags = computed(() => {
      const categories = {}
      
      // Primeiro, agrupar as tags selecionadas por categoria
      parsedTags.value.forEach(tag => {
        if (!tag.categoria) return
        
        const catId = tag.categoria.id
        if (!categories[catId]) {
          categories[catId] = {
            id: catId,
            nome: tag.categoria.nome,
            tags: []
          }
        }
        categories[catId].tags.push(tag)
      })
      
      // Agora, manter a ordem das categorias conforme configurado no workspace
      const orderedCategories = []
      props.workspaceTags.forEach(workspaceCategory => {
        if (categories[workspaceCategory.id]) {
          orderedCategories.push(categories[workspaceCategory.id])
        }
      })
      
      return orderedCategories
    })
    
    // Tags visíveis no modo minimizado (2-3 primeiras)
    const visibleTagsMinimized = computed(() => {
      return parsedTags.value.slice(0, 3)
    })
    
    // Verificar se tem mais tags
    const hasMoreTags = computed(() => {
      return parsedTags.value.length > 3
    })
    
    // Verificar se tem tags para mostrar
    const hasTagsToShow = computed(() => {
      const hasTags = parsedTags.value.length > 0
      console.log('🔍 TaskCard - hasTagsToShow:', hasTags, 'parsedTags length:', parsedTags.value.length)
      return hasTags
    })
    
    // Calcular cor de contraste (branco ou preto)
    const getContrastColor = (hexColor) => {
      if (!hexColor) return '#FFFFFF'
      
      // Remove o # se existir
      const hex = hexColor.replace('#', '')
      
      // Converte para RGB
      const r = parseInt(hex.substr(0, 2), 16)
      const g = parseInt(hex.substr(2, 2), 16)
      const b = parseInt(hex.substr(4, 2), 16)
      
      // Calcula a luminância
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      
      // Retorna branco para cores escuras, preto para cores claras
      return luminance > 0.5 ? '#000000' : '#FFFFFF'
    }

    // Função para obter ícone da tag usando o mesmo sistema do editar task
    const getTagIcon = (tag) => {
      // Usar o mesmo sistema do editar task: mapear nome do ícone para emoji
      return getIconFromMapper(tag.icone)
    }

    // Parse dos usuários atribuídos - seguindo o mesmo sistema do TaskDetailModal
    const assignedUsers = computed(() => {
      try {
        console.log('🔍 TaskCard - task.assignedUsers:', props.task.assignedUsers)
        console.log('🔍 TaskCard - workspaceUsers:', props.workspaceUsers)
        
        let assignedUsersData = []
        
        // Tentar carregar do campo assignedUsers (novo formato)
        if (props.task.assignedUsers) {
          if (Array.isArray(props.task.assignedUsers)) {
            assignedUsersData = props.task.assignedUsers
          } else if (typeof props.task.assignedUsers === 'string') {
            assignedUsersData = JSON.parse(props.task.assignedUsers)
          }
        }
        
        // Se não tem assignedUsers, tentar formato antigo
        if (assignedUsersData.length === 0) {
          if (props.task.usuariosAtribuidos && Array.isArray(props.task.usuariosAtribuidos)) {
            assignedUsersData = props.task.usuariosAtribuidos
          } else if (props.task.usuarioAtribuido) {
            assignedUsersData = [props.task.usuarioAtribuido]
          }
        }
        
        // Se temos IDs mas não objetos completos, buscar no workspaceUsers
        if (assignedUsersData.length > 0) {
          const processedUsers = assignedUsersData.map(userData => {
            // Se já é um objeto completo, usar diretamente
            if (userData && typeof userData === 'object' && userData.nomeCompleto) {
              return userData
            }
            
            // Se é apenas um ID, buscar no workspaceUsers
            const userId = typeof userData === 'object' ? userData.id : userData
            const userReal = props.workspaceUsers.find(u => u.id == userId)
            
            if (userReal) {
              console.log('✅ Usuário encontrado no workspaceUsers:', userReal.nomeCompleto)
              return userReal
            } else {
              console.warn('⚠️ Usuário não encontrado para ID:', userId)
              return {
                id: userId,
                nomeCompleto: `Usuário ${userId}`,
                email: `usuario${userId}@exemplo.com`,
                fotoPerfil: null
              }
            }
          })
          
          console.log('👥 Usuários processados:', processedUsers)
          console.log('🔍 Debug - Primeiro usuário:', processedUsers[0])
          if (processedUsers[0]) {
            console.log('🔍 Debug - nomeCompleto:', processedUsers[0].nomeCompleto)
            console.log('🔍 Debug - id:', processedUsers[0].id)
          console.log('🔍 Debug - showUsers:', props.showUsers)
          console.log('🔍 Debug - hasUsersToShow:', assignedUsers.value?.length > 0)
          console.log('🔍 Debug - user.nomeCompleto:', processedUsers[0]?.nomeCompleto)
          console.log('🔍 Debug - user.email:', processedUsers[0]?.email)
          }
          return processedUsers
        }
        
        return []
      } catch (e) {
        console.error('❌ Erro ao parsear usuários:', e)
        return []
      }
    })

    // Verificar se há usuários para mostrar
    const hasUsersToShow = computed(() => {
      try {
        return assignedUsers.value?.length > 0 || false
      } catch (e) {
        console.log('❌ Erro ao verificar usuários:', e)
        return false
      }
    })

    // Verificar se há mais usuários que o limite (3)
    const hasMoreUsers = computed(() => {
      try {
        return assignedUsers.value?.length > 3 || false
      } catch (e) {
        console.log('❌ Erro ao verificar mais usuários:', e)
        return false
      }
    })

    // Função para obter iniciais do usuário
    const getUserInitials = (nomeCompleto) => {
      if (!nomeCompleto) return '??'
      return nomeCompleto
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2)
    }
    
    // Função para obter cor baseada no nome
    const getUserColor = (nomeCompleto) => {
      if (!nomeCompleto) return '#6366f1'
      
      const colors = [
        '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
        '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
        '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
        '#ec4899', '#f43f5e'
      ]
      
      let hash = 0
      for (let i = 0; i < nomeCompleto.length; i++) {
        hash = nomeCompleto.charCodeAt(i) + ((hash << 5) - hash)
      }
      
      return colors[Math.abs(hash) % colors.length]
    }

    // Funções para o tooltip flutuante
    const handleMouseEnter = (user, event) => {
      tooltipUser.value = user
      tooltipPosition.value = {
        x: event.clientX + 10,
        y: event.clientY - 10
      }
      showTooltip.value = true
    }

    const handleMouseLeave = (user) => {
      showTooltip.value = false
      tooltipUser.value = null
    }

    // Funções para controles de atividade
    const isUserActive = (userId) => {
      return activeUsers.value.has(userId)
    }

    const toggleUserActivity = (userId) => {
      if (activeUsers.value.has(userId)) {
        activeUsers.value.delete(userId)
      } else {
        activeUsers.value.add(userId)
      }
      
      // Salvar no localStorage
      const storageKey = `task-activity-${props.task.id}-${userId}`
      try {
        if (activeUsers.value.has(userId)) {
          localStorage.setItem(storageKey, 'true')
        } else {
          localStorage.removeItem(storageKey)
        }
      } catch (e) {
        console.error('Erro ao salvar estado de atividade:', e)
      }
    }

    // Carregar estados de atividade do backend
    const loadActivityStates = () => {
      activeUsers.value.clear()
      
      try {
        // Buscar do campo activeUsersIds da task (vem do backend)
        if (props.task.activeUsersIds) {
          let activeIds = []
          
          if (Array.isArray(props.task.activeUsersIds)) {
            activeIds = props.task.activeUsersIds
          } else if (typeof props.task.activeUsersIds === 'string') {
            activeIds = JSON.parse(props.task.activeUsersIds)
          }
          
          // Adicionar usuários ativos ao Set
          activeIds.forEach(userId => {
            activeUsers.value.add(userId)
          })
          
          console.log('✅ TaskCard - Atividades carregadas do backend:', activeIds)
        }
      } catch (e) {
        console.error('❌ Erro ao carregar atividades do backend:', e)
      }
    }

    // Carregar estados quando a task mudar (atualização do backend)
    watch(() => props.task, () => {
      loadActivityStates()
    }, { deep: true, immediate: true })

    // Computed properties para mostrar apenas usuários ativos
    // BUSCA em workspaceUsers (todos do workspace), não apenas assignedUsers
    const activeUsersList = computed(() => {
      if (!props.workspaceUsers || activeUsers.value.size === 0) {
        return []
      }
      
      // Buscar usuários com atividades ativas no workspaceUsers
      const usersWithActivity = []
      
      activeUsers.value.forEach(userId => {
        const user = props.workspaceUsers.find(u => u.id === userId)
        if (user) {
          usersWithActivity.push(user)
        }
      })
      
      // console.log('👥 TaskCard - Usuários trabalhando:', usersWithActivity.map(u => u.nomeCompleto || u.email))
      return usersWithActivity
    })

    const hasActiveUsers = computed(() => {
      return activeUsersList.value.length > 0
    })

    const activeUsersCount = computed(() => {
      return activeUsersList.value.length
    })

    // Lógica de agendamento
    const isScheduled = computed(() => {
      // Verificar diferentes possíveis nomes de campos (baseado no TaskDetailModal)
      const dataAgendamento = props.task.dataAgendamento || props.task.data_agendamento || props.task.dataAgendada || props.task.data_agendada
      const horaAgendamento = props.task.horario || props.task.horaAgendamento || props.task.hora_agendamento || props.task.horaAgendada || props.task.hora_agendada
      
      return !!(dataAgendamento && horaAgendamento)
    })


    const formattedScheduleDate = computed(() => {
      const dataAgendamento = props.task.dataAgendamento || props.task.data_agendamento || props.task.dataAgendada || props.task.data_agendada
      if (!dataAgendamento) return ''
      
      try {
        // Extrair valor real (pode ser Proxy, objeto reativo, ou string)
        let dataStr = dataAgendamento
        
        // Se for objeto/Proxy, tentar extrair propriedades conhecidas ou converter para JSON
        if (typeof dataAgendamento === 'object' && dataAgendamento !== null) {
          // Se for um Proxy reativo do Vue, tentar diferentes abordagens
          dataStr = dataAgendamento.valueOf?.() || dataAgendamento.toString?.() || JSON.stringify(dataAgendamento).replace(/"/g, '')
        }
        
        // Garantir que é string
        dataStr = String(dataStr).trim()
        
        // Tentar converter de diferentes formatos para brasileiro (DD/MM/YYYY)
        
        // Formato 1: YYYY-MM-DD (formato ISO / banco de dados)
        if (/^\d{4}-\d{2}-\d{2}$/.test(dataStr)) {
          const [ano, mes, dia] = dataStr.split('-')
          if (ano && mes && dia) {
            return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`
          }
        }
        
        // Formato 2: DD/MM/YYYY (já está no formato brasileiro)
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataStr)) {
          return dataStr
        }
        
        // Formato 3: Tentar criar objeto Date e formatar
        const date = new Date(dataStr)
        if (!isNaN(date.getTime())) {
          const dia = String(date.getDate()).padStart(2, '0')
          const mes = String(date.getMonth() + 1).padStart(2, '0')
          const ano = date.getFullYear()
          return `${dia}/${mes}/${ano}`
        }
        
        // Se nenhum formato funcionar, retornar a string original
        return dataStr
      } catch (e) {
        // Em caso de erro, retornar vazio silenciosamente
        return ''
      }
    })

    const formattedScheduleTime = computed(() => {
      const horaAgendamento = props.task.horario || props.task.horaAgendamento || props.task.hora_agendamento || props.task.horaAgendada || props.task.hora_agendada
      if (!horaAgendamento) return ''
      
      try {
        // Se for apenas hora (HH:MM), adicionar segundos
        let timeStr = horaAgendamento
        if (timeStr.length === 5 && timeStr.includes(':')) {
          timeStr = timeStr + ':00'
        }
        
        const time = new Date(`2000-01-01T${timeStr}`)
        return time.toLocaleTimeString(dateLocale.value, {
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (e) {
        return horaAgendamento
      }
    })

    const motivoAgendamento = computed(() => {
      return props.task.motivo || props.task.motivoAgendamento || props.task.motivo_agendamento || props.task.motivoAgendada || props.task.motivo_agendada
    })
    
    // Buscar o motivo completo (com ícone, cor e nome) nas configurações do workspace
    const motivoCompleto = computed(() => {
      const motivoId = motivoAgendamento.value
      
      // console.log('🔍 TaskCard motivoCompleto - motivoId:', motivoId)
      // console.log('🔍 TaskCard motivoCompleto - tipo do motivoId:', typeof motivoId)
      
      if (!motivoId) {
        return null
      }
      
      // Primeiro, tentar buscar por texto (campo 'motivo' ou 'nome')
      if (props.workspaceMotivos && props.workspaceMotivos.length > 0) {
        // Buscar por texto exato
        let motivo = props.workspaceMotivos.find(m => 
          (m.motivo && m.motivo === motivoId) || 
          (m.nome && m.nome === motivoId)
        )
        
        if (motivo) {
          // console.log('✅ TaskCard motivoCompleto - Motivo encontrado por texto:', motivo)
          return {
            id: motivo.id,
            nome: motivo.motivo || motivo.nome,
            icone: motivo.icone,
            cor: motivo.cor
          }
        }
        
        // Se não encontrar por texto, tentar por ID numérico
        const motivoIdNumerico = parseInt(motivoId)
        if (!isNaN(motivoIdNumerico)) {
          motivo = props.workspaceMotivos.find(m => m.id === motivoIdNumerico)
          
          if (motivo) {
            // console.log('✅ TaskCard motivoCompleto - Motivo encontrado por ID:', motivo)
    return {
              id: motivo.id,
              nome: motivo.motivo || motivo.nome,
              icone: motivo.icone,
              cor: motivo.cor
            }
          }
        }
      }
      
      // Se não encontrar, criar um objeto com o texto como fallback
      // console.log('⚠️ TaskCard motivoCompleto - Usando texto livre como fallback:', motivoId)
      return {
        id: null,
        nome: motivoId,
        icone: 'message', // ícone padrão
        cor: '#6366f1' // cor padrão
      }
    })
    
    // Função para obter o ícone do motivo (usando o iconMapper)
    const getMotivoIcon = (iconeName) => {
      if (!iconeName) return '📋'
      
      // Usar o iconMapper para converter o nome do ícone em emoji
      const icon = getIconFromMapper(iconeName)
      
      return icon || iconeName || '📋'
    }
    
    // ========== CHECKLIST ==========
    
    // Parse do checklist da task
    const parsedChecklist = computed(() => {
      try {
        if (!props.task.checklist) return []
        
        let checklistData = props.task.checklist
        
        // Se for string, fazer parse
        if (typeof checklistData === 'string') {
          checklistData = JSON.parse(checklistData)
        }
        
        // Se for array, retornar
        if (Array.isArray(checklistData)) {
          return checklistData
        }
        
        return []
      } catch (e) {
        console.error('❌ Erro ao parsear checklist:', e)
        return []
      }
    })
    
    // Verificar se há checklist para mostrar
    const hasChecklistToShow = computed(() => {
      return parsedChecklist.value && parsedChecklist.value.length > 0
    })
    
    // Calcular estatísticas por categoria
    const checklistCategories = computed(() => {
      return parsedChecklist.value.map(category => {
        const items = category.items || []
        const completed = items.filter(item => item.completed).length
        const total = items.length
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0
        
        return {
          id: category.id,
          nome: category.nome,
          completed,
          total,
          percentage
        }
      })
    })
    
    // Calcular total geral do checklist
    const checklistTotal = computed(() => {
      let totalCompleted = 0
      let totalItems = 0
      
      parsedChecklist.value.forEach(category => {
        const items = category.items || []
        totalCompleted += items.filter(item => item.completed).length
        totalItems += items.length
      })
      
      const percentage = totalItems > 0 ? Math.round((totalCompleted / totalItems) * 100) : 0
      
      return {
        completed: totalCompleted,
        total: totalItems,
        percentage
      }
    })
    
    // ========== ANEXOS E IMAGENS ==========
    
    // Parse dos anexos da task
    const parsedAnexos = computed(() => {
      try {
        if (!props.task.anexos) return []
        
        let anexosData = props.task.anexos
        
        // Se for string, fazer parse
        if (typeof anexosData === 'string') {
          anexosData = JSON.parse(anexosData)
        }
        
        // Se for array, retornar
        if (Array.isArray(anexosData)) {
          return anexosData
        }
        
        return []
      } catch (e) {
        console.error('❌ Erro ao parsear anexos:', e)
        return []
      }
    })
    
    // Separar anexos por tipo (imagens vs arquivos)
    const anexosStats = computed(() => {
      const imagens = parsedAnexos.value.filter(anexo => {
        const extensao = anexo.nomeArquivo?.toLowerCase().split('.').pop()
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extensao)
      })
      
      // Arquivos = total - imagens (mais simples e preciso)
      const arquivos = parsedAnexos.value.filter(anexo => {
        const extensao = anexo.nomeArquivo?.toLowerCase().split('.').pop()
        return !['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extensao)
      })
      
      return {
        imagens: {
          count: imagens.length,
          hasItems: imagens.length > 0
        },
        arquivos: {
          count: arquivos.length,
          hasItems: arquivos.length > 0
        },
        total: parsedAnexos.value.length,
        hasAny: parsedAnexos.value.length > 0
      }
    })
    
    return {
      t,
      userLabel,
      mediaSrc,
      openDetailModal,
      handleCardClick,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      contextMenuVisible,
      contextMenuX,
      contextMenuY,
      showContextMenu,
      arquivarTask,
      isLightColor,
      categoriesWithTags,
      visibleTagsMinimized,
      hasMoreTags,
      hasTagsToShow,
      getContrastColor,
      getTagIcon,
      assignedUsers,
      hasUsersToShow,
      hasMoreUsers,
      getUserInitials,
      getUserColor,
      handleMouseEnter,
      handleMouseLeave,
      showTooltip,
      tooltipUser,
      tooltipPosition,
      isUserActive,
      toggleUserActivity,
      activeUsersList,
      hasActiveUsers,
      activeUsersCount,
      isScheduled,
      formattedScheduleDate,
      formattedScheduleTime,
      motivoAgendamento,
      motivoCompleto,
      getMotivoIcon,
      parsedChecklist,
      hasChecklistToShow,
      checklistCategories,
      checklistTotal,
      parsedAnexos,
      anexosStats,
      formatTitleForCard,
      // Datas
      formatDate,
      createdAtValue,
      updatedAtValue,
      // Financeiro
      recebiveisStats,
      progressClass,
      progressBarClass,
      prazoIcon,
      prazoClass
    }
  }
}
</script>

<style scoped>
/* Card principal com tema */
.task-card {
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
}

.task-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: var(--border-hover);
  transform: translateY(-1px);
}

/* Título */
.task-title {
  margin-bottom: 6px;
}

.task-title h4 {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5);
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.dark-background .task-title h4 {
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.7);
}

/* Descrição */
.task-description {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--border-primary);
}

.task-description p {
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.75);
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.4);
}

.dark-background .task-description p {
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
}

/* Tags */
.task-tags {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.15);
}

.dark-background .task-tags {
  border-top-color: rgba(255, 255, 255, 0.2);
}

/* Modo Expandido */
.tags-expanded {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tag-category {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.category-name {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.65);
}

.dark-background .category-name {
  color: rgba(255, 255, 255, 0.75);
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Modo Minimizado */
.tags-minimized {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

/* Badge da Tag */
.tag-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  font-size: 10px;
  font-weight: 500;
  border-radius: 3px;
  white-space: nowrap;
  transition: all 0.15s ease;
}

/* Ícone da Tag */
.tag-icon {
  font-size: 14px !important;
  line-height: 1 !important;
  display: inline-block !important;
  margin-right: 6px !important;
  margin-left: -2px !important;
  color: inherit !important;
  opacity: 1 !important;
  visibility: visible !important;
  width: auto !important;
  height: auto !important;
  min-width: 14px !important;
  min-height: 14px !important;
}

.tag-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Indicador "..." */
.more-tags {
  font-size: 12px;
  font-weight: 600;
  padding: 0 4px;
  color: rgba(0, 0, 0, 0.6);
}

.dark-background .more-tags {
  color: rgba(255, 255, 255, 0.7);
}

/* Datas de criação / atualização (rodapé) */
.task-meta-dates {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.65);
}

.dark-background .task-meta-dates {
  border-top-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.85);
}

.task-meta-dates-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  align-items: center;
}

.task-meta-date-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  opacity: 0.9;
}

.task-meta-label {
  font-weight: 500;
}

.task-meta-value {
  font-weight: 400;
}

.task-meta-separator {
  margin-right: 2px;
}

.task-meta-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
}

.task-meta-icon svg {
  width: 100%;
  height: 100%;
  stroke: currentColor;
  fill: none;
}

/* Usuários Atribuídos */
.task-users {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--border-primary);
}

.users-container {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.user-avatar {
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--border-primary);
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 1;
}

.user-avatar:hover {
  transform: scale(1.1);
  border-color: var(--text-primary);
  z-index: 1000;
}

.user-avatar:hover .user-tooltip {
  opacity: 1 !important;
  visibility: visible !important;
}

.user-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
}

.more-users {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--bg-secondary);
  border: 2px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: var(--text-secondary);
}

/* Tooltip flutuante global */
.floating-tooltip {
  position: fixed;
  background-color: #1f2937;
  border: 1px solid #374151;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  z-index: 99999;
  min-width: 200px;
  max-width: 280px;
  animation: tooltipFadeIn 0.2s ease-out;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tooltip-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
}

.tooltip-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #374151;
}

.tooltip-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tooltip-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: white;
}

.tooltip-info {
  flex: 1;
  min-width: 0;
}

.tooltip-name {
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tooltip-email {
  font-size: 12px;
  color: #d1d5db;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}


/* Controles de Atividade */
.task-activity-controls {
  margin-top: 6px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.activity-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 5px;
}

.activity-title {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 9px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.activity-header-icon {
  width: 12px;
  height: 12px;
  color: #10b981;
  flex-shrink: 0;
  animation: pulse 2s infinite;
}

.activity-count {
  background-color: #10b981;
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 3px;
  min-width: 16px;
  text-align: center;
  animation: pulse 2s infinite;
}

.activity-users-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.activity-user-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px;
  background-color: var(--bg-secondary);
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  transition: all 0.2s ease;
}

.activity-user-item:hover {
  background-color: var(--bg-hover);
  border-color: var(--border-hover);
}

.activity-user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.activity-user-avatar {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid var(--border-primary);
}

.activity-user-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.activity-user-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
}

.activity-user-details {
  flex: 1;
  min-width: 0;
}

.activity-user-name {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.activity-status {
  display: flex;
  align-items: center;
  gap: 4px;
}

.status-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--text-secondary);
  transition: all 0.2s ease;
}

.status-indicator.active {
  background-color: #10b981;
  box-shadow: 0 0 4px rgba(16, 185, 129, 0.4);
}

.status-text {
  font-size: 9px;
  color: var(--text-secondary);
  font-weight: 500;
}

.activity-controls {
  flex-shrink: 0;
}

.activity-indicator {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.activity-indicator:hover {
  transform: scale(1.1);
}

.activity-pulse {
  position: relative;
  animation: pulse 2s infinite;
}

.activity-idle {
  opacity: 0.5;
  transition: opacity 0.3s ease;
}

.activity-indicator:hover .activity-idle {
  opacity: 1;
}

.activity-icon {
  width: 12px;
  height: 12px;
  color: #10b981;
  transition: color 0.2s ease;
}

.activity-indicator:not(.active) .activity-icon {
  color: var(--text-secondary);
}

/* Animação de pulse para indicar atividade */
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Animação adicional de brilho */
.activity-pulse::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: glow 2s ease-in-out infinite alternate;
  pointer-events: none;
}

@keyframes glow {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.5;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.8;
  }
}

/* Animação do card quando tem usuários ativos */
.task-card.has-active-users {
  position: relative;
  overflow: hidden;
}

.task-card.has-active-users::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.1), transparent);
  animation: activityGlow 3s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
}


@keyframes activityGlow {
  0% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: 100%;
  }
}


/* Agendamento - Padrão LeadCard */
.task-schedule {
  margin-top: 6px;
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.04);
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

/* Otimização anti-flickering para agendamento */
.schedule-stable {
  will-change: auto;
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000px;
}

.schedule-text-stable,
.schedule-reason-stable h3,
.schedule-icon-stable span {
  will-change: auto;
  transition: none !important;
  animation: none !important;
}

.schedule-reason-stable,
.schedule-icon-stable {
  transform: translateZ(0);
  backface-visibility: hidden;
}

/* Header com ícone */
.schedule-header-bar {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.schedule-header-icon {
  width: 12px;
  height: 12px;
  color: #6366f1;
  flex-shrink: 0;
}

.schedule-header-text {
  font-size: 9px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

/* Data e Hora em linha */
.schedule-datetime {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background-color: transparent;
}

.schedule-date-time-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.datetime-icon {
  width: 11px;
  height: 11px;
  color: #6366f1;
  flex-shrink: 0;
}

.datetime-value {
  font-size: 10px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
}

.schedule-separator {
  color: rgba(0, 0, 0, 0.4);
  font-size: 10px;
  font-weight: bold;
}

/* Box do motivo */
.schedule-reason-box {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  background-color: transparent;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.reason-card {
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  padding: 5px 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.reason-card:hover {
  border-color: rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.reason-icon {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  flex-shrink: 0;
}

.reason-content {
  flex: 1;
}

.reason-content h3 {
  font-size: 10px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
  margin: 0;
  line-height: 1.3;
}

/* ========== CHECKLIST ========== */

.task-checklist {
  margin-top: 8px;
  padding: 8px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.checklist-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-secondary);
  margin-bottom: 10px;
}

.checklist-icon {
  width: 16px;
  height: 16px;
  color: #10b981;
  flex-shrink: 0;
}

.checklist-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 1;
}

.checklist-total-count {
  font-size: 11px;
  font-weight: 700;
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
  padding: 2px 8px;
  border-radius: 12px;
}

.checklist-categories {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checklist-category {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.category-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.checklist-category .category-name {
  font-size: 10px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dark-background .checklist-category .category-name {
  color: rgba(255, 255, 255, 0.85);
}

.category-count {
  font-size: 9px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.7);
  background: rgba(0, 0, 0, 0.06);
  padding: 2px 6px;
  border-radius: 3px;
  min-width: 16px;
  text-align: center;
}

.dark-background .category-count {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.15);
}

.category-progress-bar {
  height: 6px;
  background: var(--bg-tertiary);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}

.category-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.4);
}

.category-percentage {
  font-size: 9px;
  font-weight: 700;
  color: #10b981;
  align-self: flex-end;
}

/* Tema Claro */
.tema_clara .task-checklist {
  background: rgba(249, 250, 251, 0.6);
  border-color: rgba(229, 231, 235, 0.8);
}

.tema_clara .category-progress-bar {
  background: rgba(229, 231, 235, 0.5);
}

/* Tema Escuro */
.tema_escura .task-checklist {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(75, 85, 99, 0.3);
}

.tema_escura .category-progress-bar {
  background: rgba(55, 65, 81, 0.5);
}

/* ========== ANEXOS E IMAGENS ========== */

.task-attachments {
  margin-top: 6px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.attachments-header {
  display: flex;
  align-items: center;
  gap: 5px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 5px;
}

.attachments-icon {
  width: 12px;
  height: 12px;
  color: #f59e0b;
  flex-shrink: 0;
}

.attachments-title {
  font-size: 9px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.attachments-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  transition: all 0.2s ease;
  box-shadow: none;
}

.attachment-item:hover {
  border-color: #f59e0b;
  background: rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.attachment-item-icon {
  width: 14px;
  height: 14px;
  color: #f59e0b;
  flex-shrink: 0;
}

.attachment-item-label {
  font-size: 10px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
  flex: 1;
}

.attachment-item-count {
  font-size: 10px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.75);
  background: rgba(0, 0, 0, 0.06);
  padding: 2px 6px;
  border-radius: 3px;
  min-width: 16px;
  text-align: center;
}


/* Tema escuro - ajustes específicos */
.tema_escura .task-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.tema_escura .task-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

/* Adaptação de cores para background claro/escuro da task */
.dark-background .task-schedule {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
}

.dark-background .schedule-header-bar {
  background: rgba(255, 255, 255, 0.1);
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.dark-background .schedule-header-text {
  color: rgba(255, 255, 255, 0.85);
}

.dark-background .datetime-value {
  color: rgba(255, 255, 255, 0.85);
}

.dark-background .schedule-separator {
  color: rgba(255, 255, 255, 0.5);
}

.dark-background .schedule-reason-box {
  border-top-color: rgba(255, 255, 255, 0.15);
}

.dark-background .reason-card {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.15);
}

.dark-background .reason-card:hover {
  border-color: rgba(255, 255, 255, 0.35);
}

.dark-background .reason-content h3 {
  color: rgba(255, 255, 255, 0.85);
}

.light-background .task-checklist {
  background: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.12);
}

.dark-background .task-checklist {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
}

.dark-background .task-attachments {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
}

.dark-background .attachments-header {
  border-bottom-color: rgba(255, 255, 255, 0.15);
}

.dark-background .attachments-title {
  color: rgba(255, 255, 255, 0.85);
}

.dark-background .attachment-item {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.15);
}

.dark-background .attachment-item:hover {
  border-color: #f59e0b;
  background: rgba(255, 255, 255, 0.15);
}

.dark-background .attachment-item-label {
  color: rgba(255, 255, 255, 0.85);
}

.dark-background .attachment-item-count {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.15);
}

.light-background .checklist-title {
  color: rgba(0, 0, 0, 0.7);
}

.dark-background .checklist-title {
  color: rgba(255, 255, 255, 0.85);
}

.dark-background .task-activity-controls {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
}

.dark-background .activity-header {
  border-bottom-color: rgba(255, 255, 255, 0.15);
}

.dark-background .activity-title {
  color: rgba(255, 255, 255, 0.85);
}

/* ✨ TRANSIÇÕES SUAVES PARA ATUALIZAÇÕES EM TEMPO REAL ✨ */
.task-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.task-card * {
  transition: opacity 0.2s ease, color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

/* Animação suave para mudanças de conteúdo */
.task-title h4,
.task-description p,
.task-schedule,
.task-checklist,
.task-attachments,
.task-activity-controls {
  transition: all 0.25s ease-in-out;
}

/* Impedir piscar ao atualizar dados */
.task-card-footer,
.task-meta,
.task-users {
  will-change: contents;
  transition: opacity 0.2s ease;
}

/* Suavizar mudanças de cor */
.tag-badge,
.user-avatar,
.checklist-badge,
.schedule-icon {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Menu de Contexto */
.context-menu {
  position: fixed;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  min-width: 180px;
  padding: 4px 0;
}

.dark .context-menu {
  background: #1f2937;
  border-color: #374151;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: all 0.2s;
}

.dark .context-menu-item {
  color: #e5e7eb;
}

.context-menu-item:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.dark .context-menu-item:hover {
  background: #374151;
  color: #fff;
}

.context-menu-icon {
  width: 18px;
  height: 18px;
}

/* Customização do SweetAlert2 */
:deep(.swal-popup-custom) {
  border-radius: 12px;
  padding: 24px;
}

:deep(.swal-title-custom) {
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
}

:deep(.swal-html-custom) {
  font-size: 15px;
  line-height: 1.6;
}

:deep(.swal-html-custom p) {
  margin: 0;
}

/* Dark mode para SweetAlert2 */
.dark :deep(.swal2-popup) {
  background: #1f2937 !important;
  color: #e5e7eb !important;
}

.dark :deep(.swal2-title) {
  color: #f3f4f6 !important;
}

.dark :deep(.swal2-html-container) {
  color: #d1d5db !important;
}

.dark :deep(.swal2-icon.swal2-warning) {
  border-color: #f59e0b !important;
  color: #f59e0b !important;
}

.dark :deep(.swal2-icon.swal2-success) {
  border-color: #10b981 !important;
}

.dark :deep(.swal2-icon.swal2-success [class^='swal2-success-line']) {
  background-color: #10b981 !important;
}

.dark :deep(.swal2-icon.swal2-success .swal2-success-ring) {
  border-color: rgba(16, 185, 129, 0.3) !important;
}

.dark :deep(.swal2-icon.swal2-error) {
  border-color: #ef4444 !important;
  color: #ef4444 !important;
}

/* === Seção Financeira === */
.task-financeiro {
  margin-top: 8px;
  padding: 6px 8px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(59, 130, 246, 0.08));
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 6px;
}
.dark-background .task-financeiro {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(59, 130, 246, 0.12));
  border-color: rgba(16, 185, 129, 0.3);
}
.financeiro-header {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}
.financeiro-icon {
  width: 12px;
  height: 12px;
  color: #10b981;
  flex-shrink: 0;
}
.financeiro-title {
  font-size: 10px;
  font-weight: 600;
  color: #10b981;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.financeiro-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.financeiro-item {
  display: flex;
  align-items: center;
  gap: 4px;
}
.premium-row {
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.35);
  border: 1px solid rgba(16, 185, 129, 0.12);
  border-radius: 6px;
  padding: 4px 6px;
}
.dark-background .premium-row {
  background: rgba(17, 24, 39, 0.35);
  border-color: rgba(110, 231, 183, 0.2);
}
.financeiro-label {
  font-size: 11px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}
.dark-background .financeiro-label {
  color: rgba(255, 255, 255, 0.7);
}
.financeiro-value {
  font-size: 12px;
  font-weight: 700;
  color: #059669;
}
.dark-background .financeiro-value {
  color: #34d399;
}
.financeiro-progress-item {
  flex-direction: column;
  align-items: stretch;
}
.financeiro-progress-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
}
.financeiro-progress {
  width: 100%;
  height: 5px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 2px;
}
.dark-background .financeiro-progress {
  background-color: #4b5563;
}
.financeiro-progress-bar {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}
.bar-low {
  background: linear-gradient(90deg, #ef4444, #f97316);
}
.bar-medium {
  background: linear-gradient(90deg, #f59e0b, #eab308);
}
.bar-high {
  background: linear-gradient(90deg, #22c55e, #10b981);
}
.progress-low { color: #ef4444; }
.progress-medium { color: #f59e0b; }
.progress-high { color: #22c55e; }
.dark-background .progress-low { color: #f87171; }
.dark-background .progress-medium { color: #fbbf24; }
.dark-background .progress-high { color: #4ade80; }
.prazo-value {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.prazo-icon {
  font-size: 11px;
  line-height: 1;
}
.prazo-overdue {
  color: #ef4444 !important;
}
.prazo-near {
  color: #f59e0b !important;
}
.prazo-ok {
  color: #22c55e !important;
}
.prazo-neutral {
  color: #64748b !important;
}
</style>
